import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
// Import the new shared interface
import type { KeywordCategorization } from '@/shared/types/keyword-types';

export interface UseKeywordCategorizationResult {
  categorizations: KeywordCategorization[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Enhanced hook for keyword categorization data
 * Updated to use new KeywordCategorization interface with content_type field
 * and TOFU/MOFU/BOFU category system
 */
export const useKeywordCategorization = (
  workspaceId?: string,
  userId?: string
): UseKeywordCategorizationResult => {
  const [categorizations, setCategorizations] = useState<KeywordCategorization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategorizations = async () => {
    if (!workspaceId || !userId) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);    try {
      // First get the categorization data
      const { data: categorizationData, error: fetchError } = await supabase
        .from('keyword_categorization')
        .select(`
          *,
          content_type
        `)
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      
      let data = categorizationData;      // If we have data, enrich it with search volume from keyword_consolidated_data
      if (data && data.length > 0) {
        const keywordIds = data.map(item => item.keyword_id).filter(Boolean);
        
        if (keywordIds.length > 0) {
          const { data: keywordData, error: keywordError } = await supabase
            .from('keyword_consolidated_data')
            .select('id, search_volume, competition, difficulty, cpc')
            .in('id', keywordIds);
          
          if (!keywordError && keywordData) {
            // Create a map for quick lookup
            const keywordDataMap = new Map(keywordData.map(kd => [kd.id, kd]));
            
            // Enrich categorization data with search volume
            data = data.map(item => ({
              ...item,
              search_volume: keywordDataMap.get(item.keyword_id)?.search_volume || null,
              msv: keywordDataMap.get(item.keyword_id)?.search_volume || null, // For backward compatibility
              competition: item.competition || keywordDataMap.get(item.keyword_id)?.competition,
              kw_difficulty: item.kw_difficulty || keywordDataMap.get(item.keyword_id)?.difficulty,
              cpc: item.cpc || keywordDataMap.get(item.keyword_id)?.cpc
            }));
          }        } else {
          // Fallback: Try to match by keyword_text if keyword_id is not available
          const keywordTexts = data.map(item => item.keyword_text).filter(Boolean);
          
          if (keywordTexts.length > 0) {
            const { data: keywordDataByText, error: keywordByTextError } = await supabase
              .from('keyword_consolidated_data')
              .select('keyword, search_volume, competition, difficulty, cpc')
              .in('keyword', keywordTexts);
            
            if (!keywordByTextError && keywordDataByText) {
              // Create a map for quick lookup by keyword text
              const keywordTextDataMap = new Map(keywordDataByText.map(kd => [kd.keyword, kd]));
              
              // Enrich categorization data with search volume using keyword text matching
              data = data.map(item => {
                const matchingKeywordData = keywordTextDataMap.get(item.keyword_text);
                return {
                  ...item,
                  search_volume: matchingKeywordData?.search_volume || null,
                  msv: matchingKeywordData?.search_volume || null, // For backward compatibility
                  competition: item.competition || matchingKeywordData?.competition,
                  kw_difficulty: item.kw_difficulty || matchingKeywordData?.difficulty,
                  cpc: item.cpc || matchingKeywordData?.cpc
                };
              });
            }
          }
        }
      }
      
      // ENHANCED: Validate workspace context to detect data contamination
      if (data && data.length > 0) {
        let cleanData = [...data];
          // Check for workspace ID mismatches
        const workspaceMismatch = data.filter(item => item.workspace_id !== workspaceId);
        if (workspaceMismatch.length > 0) {// Filter out contaminated data
          cleanData = data.filter(item => item.workspace_id === workspaceId);
        }

        // ADDITIONAL: Content-based contamination detection
        const workspaceName = workspaceId.toLowerCase();        const contaminatedByContent = cleanData.filter(item => {
          const keywordText = String(item.keyword_text || '').toLowerCase();
          const categoryReasoning = String(item.category_reasoning || '').toLowerCase();
          const contentText = `${keywordText} ${categoryReasoning}`;
          
          // Define contamination patterns
          const weightLossTerms = ['weight loss', 'diet', 'fitness', 'fat loss', 'slim', 'obesity', 'calories', 'exercise', 'workout', 'nutrition'];
          const jewelryTerms = ['jewelry', 'jewellery', 'terracotta', 'ornament', 'accessory', 'necklace', 'earring', 'bracelet', 'ring'];
          
          // If this is terracotta jewelry workspace but content contains weight loss terms
          if (workspaceName.includes('terracotta') && weightLossTerms.some(term => contentText.includes(term))) {
            return true;
          }
          
          // If this is weight loss workspace but content contains jewelry terms
          if ((workspaceName.includes('weight') || workspaceName.includes('diet') || workspaceName.includes('fitness')) && 
              jewelryTerms.some(term => contentText.includes(term))) {
            return true;
          }
          
          return false;
        });
          if (contaminatedByContent.length > 0) {
          // Filter out content-contaminated data
          cleanData = cleanData.filter(item => !contaminatedByContent.includes(item));}// If we filtered out contaminated data, update state and return early
        if (cleanData.length !== categorizationData.length) {
          // Transform cleaned data
          const enhancedCategorizations: KeywordCategorization[] = cleanData.map(item => ({
            ...item,
            category: item.category as 'TOFU' | 'MOFU' | 'BOFU',
            content_type: item.content_type || undefined
          } as KeywordCategorization));
          
          setCategorizations(enhancedCategorizations);
          setIsLoading(false);
          return;        }
      }
      
      // Transform data to ensure category values are properly mapped
      // The database migration should have already updated old categories to TOFU/MOFU/BOFU
      const enhancedCategorizations: KeywordCategorization[] = (data || []).map(item => ({
        ...item,
        // Ensure category is one of the valid funnel stages
        category: item.category as 'TOFU' | 'MOFU' | 'BOFU',
        // Include content_type field
        content_type: item.content_type || undefined
      } as KeywordCategorization));
      
      setCategorizations(enhancedCategorizations);
    } catch (err: any) {
      console.error('Error fetching keyword categorizations:', err);
      setError(err.message || 'Failed to fetch categorizations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategorizations();
  }, [workspaceId, userId]);

  return {
    categorizations,
    isLoading,
    error,
    refetch: fetchCategorizations
  };
};